import { useState } from "react";
import { useLocation } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Capacitor } from "@capacitor/core";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";
import UnverifiedAccountActions from "@/components/UnverifiedAccountActions";

const onboardingSchema = z.object({
  zipCode: z.string().min(1, "ZIP code is required"),
  roles: z.enum(["buyer", "seller", "both"], {
    required_error: "Select how you want to use Stoop",
  }),
});

type OnboardingFormValues = z.infer<typeof onboardingSchema>;

function getDefaultRouteForRole(role: "buyer" | "seller" | "both") {
  // Sellers (and both) land directly in the listing flow — listing the first
  // product is the highest-leverage activation step. A new seller without a
  // profile gets redirected through /seller/setup, which ends on the same
  // "Add my first listing" step, so either way they arrive at listing.
  if (role === "seller" || role === "both") return "/seller?action=add";
  return "/";
}

// Best-effort current-position grab. We use this to cross-check the entered
// ZIP against where the device actually is. Returns null on denial, timeout,
// unsupported, or any error — by design, so missing GPS never blocks signup.
// Mirrors the Capacitor-native / navigator fallback used in useBuyerLocation.
async function tryGetGps(): Promise<{ latitude: number; longitude: number } | null> {
  try {
    if (Capacitor.isNativePlatform()) {
      const { Geolocation } = await import("@capacitor/geolocation");
      const perm = await Geolocation.requestPermissions();
      if (perm.location === "denied") return null;
      const pos = await Geolocation.getCurrentPosition({
        enableHighAccuracy: false,
        timeout: 3000,
        maximumAge: 60000,
      });
      return { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
    }

    if (typeof navigator === "undefined" || !("geolocation" in navigator)) return null;

    return await new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) =>
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          }),
        () => resolve(null),
        { enableHighAccuracy: false, timeout: 3000, maximumAge: 300000 },
      );
    });
  } catch {
    return null;
  }
}

export default function Onboarding() {
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<OnboardingFormValues>({
    resolver: zodResolver(onboardingSchema),
    defaultValues: {
      zipCode: user?.zipCode ?? "",
      roles: (user?.roles as "buyer" | "seller" | "both" | undefined) ?? "buyer",
    },
  });

  const onSubmit = async (values: OnboardingFormValues) => {
    setSubmitError(null);
    try {
      // Grab GPS if the user allows it so the server can confirm the ZIP
      // matches where they are. Optional — null just means ZIP-only checking.
      // GPS is best-effort ZIP confirmation and must never block the button. Cap
      // the whole attempt so onboarding always proceeds — a GPS-less review device
      // previously hung here for 10s and read as an unresponsive button.
      const gps = await Promise.race([
        tryGetGps(),
        new Promise<null>((resolve) => setTimeout(() => resolve(null), 3500)),
      ]);
      const response = await apiRequest("POST", "/api/onboarding", {
        ...values,
        ...(gps
          ? { gpsLatitude: gps.latitude, gpsLongitude: gps.longitude }
          : {}),
      });
      const updatedUser = await response.json();
      queryClient.setQueryData(["/api/auth/user"], updatedUser);
      setLocation(getDefaultRouteForRole(values.roles));
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : "Unable to complete onboarding.",
      );
    }
  };

  return (
    <div className="min-h-screen bg-muted/20 py-10 px-4 flex flex-col items-center justify-center gap-4">
      <Card className="w-full max-w-xl shadow-lg">
        <CardHeader>
          <CardTitle>Complete your setup</CardTitle>
          <CardDescription>
            Tell us your ZIP code and whether you want to buy, sell, or both.
          </CardDescription>
        </CardHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <CardContent className="space-y-6">
              <FormField
                control={form.control}
                name="zipCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>ZIP code</FormLabel>
                    <FormControl>
                      <Input placeholder="12180" {...field} />
                    </FormControl>
                    <p className="text-xs text-muted-foreground">
                      We'll ask for your location to confirm your ZIP — it keeps the
                      marketplace local and real. You can decline; we'll just use your ZIP.
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="roles"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>I want to use Stoop as a...</FormLabel>
                    <div className="grid gap-3 sm:grid-cols-3">
                      {[
                        { value: "buyer", label: "Buyer" },
                        { value: "seller", label: "Grower / Maker" },
                        { value: "both", label: "Buyer + Grower / Maker" },
                      ].map((option) => {
                        const selected = field.value === option.value;
                        return (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => field.onChange(option.value)}
                            className={`rounded-lg border p-4 text-left transition ${
                              selected
                                ? "border-primary bg-primary/5"
                                : "border-border bg-background hover:border-primary/50"
                            }`}
                          >
                            <div className="font-medium">{option.label}</div>
                          </button>
                        );
                      })}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {submitError && <p className="text-sm text-destructive">{submitError}</p>}
            </CardContent>

            <CardFooter>
              <Button type="submit" disabled={form.formState.isSubmitting} className="w-full">
                {form.formState.isSubmitting ? "Saving…" : "Continue"}
              </Button>
            </CardFooter>
          </form>
        </Form>
      </Card>
      <UnverifiedAccountActions />
    </div>
  );
}
